import { onBeforeUnmount, reactive, ref, watch } from 'vue'

/**
 * Positions a dropdown popover with `position: fixed` so it escapes any
 * `overflow: auto/hidden` ancestor (e.g. a modal body) instead of being
 * clipped. Computes the rect from the trigger's `getBoundingClientRect()` and
 * flips above the trigger when the available space below is insufficient.
 *
 * Vue 2.7 has no built-in <Teleport> and the app has no floating-ui dep, so
 * this is the dependency-free escape-hatch for the modal-clipping bug.
 *
 * @param triggerRef  ref to the trigger element (button/input).
 * @param open        ref controlling popover visibility.
 * @param maxHeight   popover content max-height in px (for flip math).
 */
export function useAnchorPopover(
  triggerRef: { value: HTMLElement | null },
  open: { value: boolean },
  maxHeight = 240,
  fitContent = false,
  menuRef?: { value: HTMLElement | null },
) {
  // `position: fixed` + pixel coords => the popover paints relative to the
  // viewport, ignoring every ancestor's overflow/transform.
  const gap = 4
  const style = reactive({
    position: 'fixed' as const,
    top: '0px',
    left: '0px',
    width: fitContent ? ('auto' as const) : '0px',
    maxHeight: fitContent ? ('none' as const) : `${maxHeight}px`,
  })
  const placement = ref<'bottom' | 'top'>('bottom')

  function compute(): void {
    const el = triggerRef.value
    if (!el) return
    const rect = el.getBoundingClientRect()
    // When fitContent, measure the actual rendered height for flip math;
    // fall back to maxHeight before the first paint.
    const contentHeight =
      fitContent && menuRef?.value ? menuRef.value.offsetHeight : maxHeight
    const spaceBelow = window.innerHeight - rect.bottom
    const spaceAbove = rect.top
    const flip = spaceBelow < contentHeight && spaceAbove > spaceBelow
    placement.value = flip ? 'top' : 'bottom'
    style.top = flip
      ? `${Math.max(0, rect.top - contentHeight - gap)}px`
      : `${rect.bottom + gap}px`
    style.left = `${rect.left}px`
    if (!fitContent) style.width = `${rect.width}px`
  }

  let raf = 0
  function schedule(): void {
    if (raf) return
    raf = requestAnimationFrame(() => {
      raf = 0
      compute()
    })
  }

  function onScrollOrResize(): void {
    if (open.value) schedule()
  }

  watch(
    () => open.value,
    (isOpen) => {
      if (isOpen) {
        // Wait a frame so the popover is mounted before measuring and so the
        // trigger's rect reflects the post-open layout.
        requestAnimationFrame(() => {
          compute()
          window.addEventListener('scroll', onScrollOrResize, true)
          window.addEventListener('resize', onScrollOrResize)
        })
      } else {
        window.removeEventListener('scroll', onScrollOrResize, true)
        window.removeEventListener('resize', onScrollOrResize)
      }
    },
  )

  onBeforeUnmount(() => {
    if (raf) cancelAnimationFrame(raf)
    window.removeEventListener('scroll', onScrollOrResize, true)
    window.removeEventListener('resize', onScrollOrResize)
  })

  return { style, placement }
}
